import { flushSync } from 'react-dom'

/**
 * SCRUM-1032 QC, 5 Oct — closes whatever cell editor is open and runs its
 * commit before returning, so a value the commit writes to a ref is there for
 * the caller to read.
 *
 * A save has to start by committing the editor the user left open, or the value
 * in it is never written. `stopEditing()` alone does not do that: it only sets
 * the grid's editing cell to null, and the editor commits from its own effect
 * cleanup (`TextEditor`), so the value lands one React commit later.
 *
 * That gap used to be bridged with `await new Promise(r => setTimeout(r, 0))`.
 * A macrotask is the wrong tool: Chrome throttles timers in a hidden tab to one
 * callback a second, and after a minute to one a minute, so the save parked on
 * that yield. Measured on D2 on 6 Oct with 431 decoders — the autosave called
 * the save, the save never reached its first statement, and while it sat there
 * the grid stayed read-only, `inFlightRef` stayed set so every later autosave
 * was dropped as "already saving", and the wizard's own flush would have
 * awaited a pass that was not running. One renamed decoder stayed on screen and
 * never reached the server: Wei Woon's "the edited name shows the last saved
 * value".
 *
 * `flushSync` runs the unmount and its cleanup before returning, so the
 * editor's commit callback has RUN by the time this call does, and nothing is
 * waiting on a timer.
 *
 * What it does not guarantee: that the state that callback set has been
 * rendered. The cleanup's `setState` is scheduled at normal priority and
 * renders later, so a ref assigned during render (`ref.current = state`)
 * still holds the old value here. A caller must read what the commit
 * callback wrote synchronously — a ref written inside it — not React state or
 * a render-assigned mirror of it (SCRUM-1032 review: Plants / Soil / Emitter /
 * ET missed an edit this way when a save began with an editor open).
 */
export const commitOpenEditor = (
  api: { stopEditing: (cancel?: boolean) => void } | null | undefined,
) => {
  if (!api) return
  flushSync(() => {
    api.stopEditing()
  })
}

/** What the guided autosave asks of a save it starts with an editor open. */
export interface GuidedSaveOptions {
  /**
   * The editor has been open past the autosave's hold. Save the other rows
   * and leave this one, and its editor, alone.
   */
  leaveEditorOpen?: boolean
}

/**
 * SCRUM-1032 QC, 30 Oct — the start of a staged-grid save.
 *
 * Normally it commits the open editor (see `commitOpenEditor`). When the
 * autosave asks to leave the editor open, it commits nothing and returns the
 * row being edited, which the save must skip. Closing that editor lost what
 * the user typed next. A new row's create also gave it a new id, which
 * unmounted the editor and moved focus to the page.
 */
export const commitOrHoldOpenEditor = (
  api:
    | {
        stopEditing: (cancel?: boolean) => void
        getEditingCell?: () => { rowId: string } | null
      }
    | null
    | undefined,
  options?: GuidedSaveOptions,
): string | null => {
  const open = options?.leaveEditorOpen ? api?.getEditingCell?.() : null
  if (open) return String(open.rowId)
  commitOpenEditor(api)
  return null
}
